import { CommonModule } from '@angular/common';
import {
  Component,
  input,
  output,
  signal,
  ElementRef,
  viewChild,
  HostListener,
} from '@angular/core';

import { TranslateLangPipe } from '../../pipes/translate-lang.pipe';

/**
 * Represents a single action item in the menu
 */
export interface ActionMenuItem {
  /**
   * Unique identifier for the action
   */
  id: string;
  /**
   * Display label for the action
   */
  label: string;
  /**
   * Optional icon (not used in current design but available for future)
   */
  icon?: string;
  /**
   * Optional custom data associated with the action
   */
  data?: any;
}

/**
 * An action menu component that displays a list of actions in a dropdown.
 * Designed based on Figma design for user action menus.
 * The menu appears on hover over a trigger element (e.g., 3-dot button).
 * Figma URL: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=9528-64498
 *
 * @example
 * ```html
 * <div class="action-menu-container">
 *   <button class="action-btn" (click)="toggleMenu()">
 *     <svg>...</svg>
 *   </button>
 *   <app-action-menu
 *     [items]="actions"
 *     [isOpen]="menuOpen"
 *     (itemSelected)="onActionSelected($event)"
 *     (closed)="menuOpen = false">
 *   </app-action-menu>
 * </div>
 * ```
 */
@Component({
  selector: 'app-action-menu',
  standalone: true,
  imports: [CommonModule, TranslateLangPipe],
  templateUrl: './action-menu.component.html',
  styleUrls: ['./action-menu.component.scss'],
})
export class ActionMenuComponent {
  /**
   * Array of action items to display in the menu
   */
  items = input<ActionMenuItem[]>([]);

  /**
   * Whether the menu is currently visible
   */
  isOpen = input<boolean>(false);

  /**
   * Emits when a user selects an action from the menu
   */
  itemSelected = output<ActionMenuItem>();

  /**
   * Emits when the menu should close
   */
  closed = output<void>();

  /**
   * Reference to the menu container element
   */
  menuContainer = viewChild<ElementRef>('menuContainer');

  /**
   * Index of the currently hovered item
   */
  hoveredIndex = signal<number>(-1);

  /**
   * Handles action selection
   */
  selectAction(item: ActionMenuItem): void {
    this.itemSelected.emit(item);
    this.closed.emit();
  }

  /**
   * Handles mouse enter on an item
   */
  onItemMouseEnter(index: number): void {
    this.hoveredIndex.set(index);
  }

  /**
   * Handles mouse leave on an item
   */
  onItemMouseLeave(): void {
    this.hoveredIndex.set(-1);
  }

  /**
   * Checks if an item is currently hovered
   */
  isItemHovered(index: number): boolean {
    return this.hoveredIndex() === index;
  }

  /**
   * Closes the menu when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const container = this.menuContainer();
    if (this.isOpen() && container) {
      const clickedInside = container.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.closed.emit();
      }
    }
  }
}
